import type {
  BetweenOperator,
  CellIsBetweenRule,
  CellIsOperator,
  CellIsRule,
  CellStyle,
  ColorScaleRule,
  ConditionalFormatRule,
  ContainsTextRule,
  DataBarRule,
  ExpressionRule,
  ScaleValueType,
  SheetSpec,
  TextOperator,
} from '@meridian-ssa/shared';
import type * as SpreadSheetsTypes from '@mescius/spread-sheets';

import { logWarn } from '@/lib/logger';

import { getSpreadJS } from './loader';

type Worksheet = SpreadSheetsTypes.Spread.Sheets.Worksheet;
type Workbook = SpreadSheetsTypes.Spread.Sheets.Workbook;
type Cell = SheetSpec['sheets'][0]['cells'][0];
type Range = SpreadSheetsTypes.Spread.Sheets.Range;

/**
 * Set cell content (value or formula) in a worksheet.
 */
export function setCellContent(
  sheet: Worksheet,
  cell: Cell,
  row: number,
  col: number
) {
  if (cell.type === 'formula' && cell.formula) {
    const formula = cell.formula.startsWith('=')
      ? cell.formula.substring(1)
      : cell.formula;

    try {
      sheet.setFormula(row, col, formula);

      // Highlight formula cells with subtle background if no custom fill
      if (!cell.style?.fill) {
        sheet.getCell(row, col).backColor('rgba(239, 235, 229, 0.3)');
      }
    } catch (error) {
      // Handle invalid formulas gracefully
      logWarn(`Invalid formula in cell ${cell.a1}: ${formula}`);

      // Track invalid formula cells at sheet level for efficient lookup
      const sheetInstance = sheet as any;
      const currentTag = sheetInstance.tag?.() || {};
      const invalidCells = currentTag.invalidFormulaCells || [];
      invalidCells.push({ row, col, formula });
      sheetInstance.tag({ ...currentTag, invalidFormulaCells: invalidCells });

      // Display #INVALID in the cell
      sheet.setValue(row, col, '#INVALID');

      // Highlight error cells with a light red background
      if (!cell.style?.fill) {
        sheet.getCell(row, col).backColor('rgba(255, 200, 200, 0.3)');
      }
    }
  } else if (cell.value !== undefined) {
    sheet.setValue(row, col, cell.value);
  }
}

/**
 * Create a SpreadJS Style from a CellStyle.
 */
function createStyle(
  cellStyle: CellStyle
): SpreadSheetsTypes.Spread.Sheets.Style | null {
  const GC = getSpreadJS();
  if (!GC) return null;

  const style = new GC.Spread.Sheets.Style();

  if (cellStyle.fill) {
    style.backColor = cellStyle.fill;
  }

  if (cellStyle.fontColor) {
    style.foreColor = cellStyle.fontColor;
  }

  if (cellStyle.fontSize || cellStyle.fontWeight) {
    const weight = cellStyle.fontWeight === 'bold' ? 'bold' : 'normal';
    const size = cellStyle.fontSize || 11;
    style.font = `${weight} ${size}pt Calibri`;
  }

  if (cellStyle.numberFormat) {
    style.formatter = cellStyle.numberFormat;
  }

  if (cellStyle.border) {
    const borderColor = cellStyle.border.color || '#000000';
    const borderMap: {
      [key: string]: SpreadSheetsTypes.Spread.Sheets.LineStyle;
    } = {
      thin: GC.Spread.Sheets.LineStyle.thin,
      medium: GC.Spread.Sheets.LineStyle.medium,
      thick: GC.Spread.Sheets.LineStyle.thick,
    };

    if (cellStyle.border.top) {
      style.borderTop = new GC.Spread.Sheets.LineBorder(
        borderColor,
        borderMap[cellStyle.border.top] || GC.Spread.Sheets.LineStyle.thin
      );
    }
    if (cellStyle.border.right) {
      style.borderRight = new GC.Spread.Sheets.LineBorder(
        borderColor,
        borderMap[cellStyle.border.right] || GC.Spread.Sheets.LineStyle.thin
      );
    }
    if (cellStyle.border.bottom) {
      style.borderBottom = new GC.Spread.Sheets.LineBorder(
        borderColor,
        borderMap[cellStyle.border.bottom] || GC.Spread.Sheets.LineStyle.thin
      );
    }
    if (cellStyle.border.left) {
      style.borderLeft = new GC.Spread.Sheets.LineBorder(
        borderColor,
        borderMap[cellStyle.border.left] || GC.Spread.Sheets.LineStyle.thin
      );
    }
  }

  return style;
}

/**
 * Apply cell styling from SheetSpec to a worksheet cell.
 */
export function applyCellStyle(
  sheet: Worksheet,
  cell: Cell,
  row: number,
  col: number
) {
  if (!cell.style) return;

  const style = createStyle(cell.style);
  if (style) {
    sheet.setStyle(row, col, style);
  }
}

/**
 * Convert a single cell reference to absolute (A1 -> $A$1)
 */
function makeAbsoluteCell(cell: string): string {
  const match = cell.match(/^([A-Z]+)([0-9]+)$/);
  if (!match) return cell;
  const [, col, row] = match;
  return `$${col}$${row}`;
}

/**
 * Convert relative cell references to absolute references
 * Examples: E3:F5 -> $E$3:$F$5, Budget!E3:F5 -> Budget!$E$3:$F$5
 */
function makeAbsoluteReference(ref: string): string {
  // Split by sheet name if present
  const parts = ref.split('!');
  const sheetPrefix = parts.length > 1 ? parts[0] + '!' : '';
  const range = parts.length > 1 ? parts[1] : parts[0];

  // If already absolute, return as-is
  if (range.includes('$')) {
    return ref;
  }

  // Handle ranges (A1:B10) vs single cells (A1)
  if (range.includes(':')) {
    const [start, end] = range.split(':');
    return `${sheetPrefix}${makeAbsoluteCell(start)}:${makeAbsoluteCell(end)}`;
  } else {
    return `${sheetPrefix}${makeAbsoluteCell(range)}`;
  }
}

/**
 * Add named ranges from SheetSpec to a workbook.
 */
export function addNamedRanges(
  spread: Workbook,
  namedRanges: Array<{ name: string; ref: string }>,
  sheetName: string
) {
  namedRanges.forEach(namedRange => {
    try {
      // If ref doesn't include sheet name, add it
      let ref = namedRange.ref.includes('!')
        ? namedRange.ref
        : `${sheetName}!${namedRange.ref}`;

      // Make reference absolute with $ signs
      ref = makeAbsoluteReference(ref);

      // For absolute references, use 0,0 for baseRow/baseCol
      spread.addCustomName(namedRange.name, ref, 0, 0);
    } catch (e) {
      logWarn(`Failed to add named range ${namedRange.name}`);
    }
  });
}

/**
 * Setup comprehensive read-only protection for a workbook by blocking all mutation operations
 * while still allowing users to view formulas with F2.
 * Based on SpreadJS Events API: https://developer.mescius.com/spreadjs/api/classes/GC.Spread.Sheets.Events
 */
export function setupReadOnlyProtection(
  spread: Workbook,
  originalData: Map<string, any>
) {
  const GC = getSpreadJS();
  if (!GC) return;

  // Generic cancel handler for blocking events
  const cancelEvent = (e: any, args: any) => {
    args.cancel = true;
  };

  // Block all mutation events by canceling them
  const mutationEvents = [
    // Clipboard operations
    GC.Spread.Sheets.Events.ClipboardPasting,
    GC.Spread.Sheets.Events.ClipboardChanging,
    // Drag operations
    GC.Spread.Sheets.Events.DragDropBlock,
    GC.Spread.Sheets.Events.DragDropBlockCompleted,
    GC.Spread.Sheets.Events.DragFillBlock,
    GC.Spread.Sheets.Events.DragFillBlockCompleted,
    // Edit operations
    GC.Spread.Sheets.Events.EditChange,
    GC.Spread.Sheets.Events.UserFormulaEntered,
    // Range and bulk operations
    GC.Spread.Sheets.Events.RangeChanged,
    // Row and column changes
    GC.Spread.Sheets.Events.ColumnChanged,
    GC.Spread.Sheets.Events.RowChanged,
  ];

  // Bind cancel handler to all mutation events
  mutationEvents.forEach(event => spread.bind(event, cancelEvent));

  // Special case: CellChanged - only block value/formula changes
  spread.bind(GC.Spread.Sheets.Events.CellChanged, (e: any, args: any) => {
    if (args.propertyName === 'value' || args.propertyName === 'formula') {
      args.cancel = true;
    }
  });

  // Final backup: revert value changes that slip through
  const valueChangedHandler = (e: any, args: any) => {
    if (!GC) return;

    const sheet = args.sheet;
    const sheetIndex = spread.getSheetIndex(sheet.name());
    const key = `${sheetIndex}-${args.row}-${args.col}`;
    const original = originalData.get(key);

    // Temporarily unbind to prevent infinite loop
    spread.unbind(GC.Spread.Sheets.Events.ValueChanged, valueChangedHandler);

    if (original) {
      if (original.formula) {
        sheet.setFormula(args.row, args.col, original.formula);
      } else if (original.value !== undefined) {
        sheet.setValue(args.row, args.col, original.value);
      } else {
        sheet.setValue(args.row, args.col, null);
      }
    } else {
      sheet.setValue(args.row, args.col, null);
    }

    spread.bind(GC.Spread.Sheets.Events.ValueChanged, valueChangedHandler);
  };

  spread.bind(GC.Spread.Sheets.Events.ValueChanged, valueChangedHandler);
}

/**
 * Toggle display of invalid formulas between formula text and #INVALID.
 * Uses sheet's tag to store list of invalid formula cells for efficient lookup.
 */
export function toggleInvalidFormulaDisplay(
  sheet: Worksheet,
  showFormulas: boolean
) {
  // Get list of invalid formula cells from sheet tag
  const sheetTag = (sheet as any).tag?.() || {};
  const invalidCells: Array<{ row: number; col: number; formula: string }> =
    sheetTag.invalidFormulaCells || [];

  invalidCells.forEach(({ row, col, formula }) => {
    if (showFormulas) {
      // Show the formula text
      sheet.setValue(row, col, `=${formula}`);
    } else {
      // Show #INVALID
      sheet.setValue(row, col, '#INVALID');
    }
  });
}

/**
 * Restore workbook to its original state using SpreadJS native serialization.
 * This uses toJSON/fromJSON for efficient full-workbook restoration.
 */
export function restoreOriginalState(spread: Workbook, originalSnapshot: any) {
  if (!originalSnapshot) {
    logWarn('No original snapshot available to restore');
    return;
  }

  // Restore the entire workbook from the snapshot
  spread.fromJSON(originalSnapshot);
}

/**
 * Parse A1 notation to row/col indices (0-indexed).
 */
export function parseA1(a1: string): { row: number; col: number } {
  const match = a1.match(/^([A-Z]+)(\d+)$/);
  if (!match) throw new Error(`Invalid A1 notation: ${a1}`);

  const colStr = match[1];
  const rowStr = match[2];

  // Convert column letters to number (A=0, B=1, ..., Z=25, AA=26, etc.)
  let col = 0;
  for (let i = 0; i < colStr.length; i++) {
    col = col * 26 + (colStr.charCodeAt(i) - 65 + 1);
  }
  col -= 1; // Make it 0-indexed

  const row = parseInt(rowStr, 10) - 1; // Make it 0-indexed

  return { row, col };
}

// ============================================================================
// Conditional Formatting Support (SheetSpec@2)
// ============================================================================

/**
 * Strip sheet name prefix from a range reference.
 * Handles both quoted ('Sheet Name'!A1) and unquoted (Sheet1!A1) formats.
 */
function stripSheetPrefix(ref: string): string {
  // Handle quoted sheet names: 'Sheet Name'!A1:B10
  if (ref.startsWith("'")) {
    const endQuote = ref.indexOf("'", 1);
    if (endQuote !== -1 && ref[endQuote + 1] === '!') {
      return ref.substring(endQuote + 2);
    }
  }
  // Handle unquoted sheet names: Sheet1!A1:B10
  const bangIndex = ref.indexOf('!');
  if (bangIndex !== -1) {
    return ref.substring(bangIndex + 1);
  }
  return ref;
}

/**
 * Parse range string to SpreadJS Range array.
 * Handles single cells (A1), ranges (A1:B10), multiple ranges (A1:B10,C1:D10),
 * and sheet-qualified ranges ('Sheet Name'!A1:B10).
 */
function parseRange(range: string): Range[] {
  const GC = getSpreadJS();
  if (!GC) return [];

  // Strip sheet name prefix (conditional formats are applied per-sheet anyway)
  const rangeWithoutSheet = stripSheetPrefix(range);

  // Split multiple ranges by comma
  const rangeStrings = rangeWithoutSheet.split(',').map(s => s.trim());
  const ranges: Range[] = [];

  for (const rangeStr of rangeStrings) {
    try {
      if (rangeStr.includes(':')) {
        // Range like A1:B10
        const [start, end] = rangeStr.split(':');
        const startPos = parseA1(start);
        const endPos = parseA1(end);
        ranges.push(
          new GC.Spread.Sheets.Range(
            startPos.row,
            startPos.col,
            endPos.row - startPos.row + 1,
            endPos.col - startPos.col + 1
          )
        );
      } else {
        // Single cell like A1
        const pos = parseA1(rangeStr);
        ranges.push(new GC.Spread.Sheets.Range(pos.row, pos.col, 1, 1));
      }
    } catch (e) {
      logWarn(`Failed to parse range reference: ${rangeStr}`);
    }
  }

  return ranges;
}

/**
 * Get context needed for conditional formatting rules.
 * Returns null if SpreadJS isn't available or range is invalid.
 */
function getConditionalFormatContext(range: string) {
  const GC = getSpreadJS();
  if (!GC) return null;

  const ranges = parseRange(range);
  if (ranges.length === 0) return null;

  return {
    GC,
    CF: GC.Spread.Sheets.ConditionalFormatting,
    ranges,
  };
}

/**
 * Map comparison operators to SpreadJS ComparisonOperators.
 */
function mapComparisonOperator(op: CellIsOperator | BetweenOperator): number {
  const GC = getSpreadJS();
  if (!GC) return 0;

  const CF = GC.Spread.Sheets.ConditionalFormatting;
  const mapping: Record<CellIsOperator | BetweenOperator, number> = {
    equal: CF.ComparisonOperators.equalsTo,
    notEqual: CF.ComparisonOperators.notEqualsTo,
    greaterThan: CF.ComparisonOperators.greaterThan,
    greaterThanOrEqual: CF.ComparisonOperators.greaterThanOrEqualsTo,
    lessThan: CF.ComparisonOperators.lessThan,
    lessThanOrEqual: CF.ComparisonOperators.lessThanOrEqualsTo,
    between: CF.ComparisonOperators.between,
    notBetween: CF.ComparisonOperators.notBetween,
  };
  return mapping[op] ?? CF.ComparisonOperators.equalsTo;
}

/**
 * Map Excel text operators to SpreadJS TextComparisonOperators.
 */
function mapTextOperator(op: TextOperator): number {
  const GC = getSpreadJS();
  if (!GC) return 0;

  const CF = GC.Spread.Sheets.ConditionalFormatting;
  const mapping: Record<TextOperator, number> = {
    containsText: CF.TextComparisonOperators.contains,
    notContains: CF.TextComparisonOperators.doesNotContain,
    beginsWith: CF.TextComparisonOperators.beginsWith,
    endsWith: CF.TextComparisonOperators.endsWith,
  };
  return mapping[op] ?? CF.TextComparisonOperators.contains;
}

/**
 * Map scale value types to SpreadJS ScaleValueType.
 */
function mapScaleValueType(type: ScaleValueType): number {
  const GC = getSpreadJS();
  if (!GC) return 0;

  const CF = GC.Spread.Sheets.ConditionalFormatting;
  const mapping: Record<ScaleValueType, number> = {
    number: CF.ScaleValueType.number,
    min: CF.ScaleValueType.lowestValue,
    max: CF.ScaleValueType.highestValue,
    percent: CF.ScaleValueType.percent,
    percentile: CF.ScaleValueType.percentile,
    formula: CF.ScaleValueType.formula,
  };
  return mapping[type] ?? CF.ScaleValueType.number;
}

/**
 * Apply cellIs or cellIsBetween rule (cell value comparison).
 */
function applyCellValueRule(
  sheet: Worksheet,
  rule: CellIsRule | CellIsBetweenRule
): void {
  const ctx = getConditionalFormatContext(rule.range);
  if (!ctx) return;

  if (!rule.style) {
    logWarn(
      `${rule.type} rule missing required style property. range: ${rule.range}`
    );
    return;
  }

  const { CF, ranges } = ctx;
  const cfRule = new (CF.NormalConditionRule as any)();
  cfRule.ruleType(CF.RuleType.cellValueRule);
  cfRule.ranges(ranges);
  cfRule.operator(mapComparisonOperator(rule.operator));

  // Parse values based on rule type
  const parseValue = (v: string) => (isNaN(Number(v)) ? v : Number(v));

  if (rule.type === 'cellIs') {
    cfRule.value1(parseValue(rule.value));
  } else {
    cfRule.value1(parseValue(rule.from));
    cfRule.value2(parseValue(rule.to));
  }

  cfRule.style(createStyle(rule.style));
  sheet.conditionalFormats.addRule(cfRule);
}

/**
 * Apply expression rule (formula-based conditional formatting).
 */
function applyExpressionRule(sheet: Worksheet, rule: ExpressionRule): void {
  const ctx = getConditionalFormatContext(rule.range);
  if (!ctx) return;

  if (!rule.style) {
    logWarn(
      `expression rule missing required style property. range: ${rule.range}`
    );
    return;
  }

  const { CF, ranges } = ctx;
  const cfRule = new (CF.NormalConditionRule as any)();
  cfRule.ruleType(CF.RuleType.formulaRule);
  cfRule.ranges(ranges);

  // Remove leading = if present (SpreadJS adds it)
  const formula = rule.formula.startsWith('=')
    ? rule.formula.substring(1)
    : rule.formula;
  cfRule.formula(formula);

  cfRule.style(createStyle(rule.style));
  sheet.conditionalFormats.addRule(cfRule);
}

/**
 * Apply containsText rule (text matching).
 */
function applyContainsTextRule(sheet: Worksheet, rule: ContainsTextRule): void {
  const ctx = getConditionalFormatContext(rule.range);
  if (!ctx) return;

  const { ranges } = ctx;

  if (!rule.style) {
    logWarn(
      `containsText rule missing required style property. range: ${rule.range},`
    );
    return;
  }

  const style = createStyle(rule.style);
  if (!style) return;

  sheet.conditionalFormats.addSpecificTextRule(
    mapTextOperator(rule.operator),
    rule.text,
    style,
    ranges
  );
}

/**
 * Apply colorScale rule (2 or 3 color gradient).
 */
function applyColorScaleRule(sheet: Worksheet, rule: ColorScaleRule): void {
  const ctx = getConditionalFormatContext(rule.range);
  if (!ctx) return;

  const { ranges } = ctx;
  const colors = rule.colors;

  // Helper to convert value to number (SpreadJS expects number for scale values)
  const toNumber = (value: number | string | undefined): number => {
    if (value === undefined) return 0;
    return typeof value === 'string' ? parseFloat(value) || 0 : value;
  };

  if (colors.length === 2) {
    // 2-color scale
    sheet.conditionalFormats.add2ScaleRule(
      mapScaleValueType(colors[0].type),
      toNumber(colors[0].value),
      colors[0].color,
      mapScaleValueType(colors[1].type),
      toNumber(colors[1].value),
      colors[1].color,
      ranges
    );
  } else if (colors.length === 3) {
    // 3-color scale
    sheet.conditionalFormats.add3ScaleRule(
      mapScaleValueType(colors[0].type),
      toNumber(colors[0].value),
      colors[0].color,
      mapScaleValueType(colors[1].type),
      toNumber(colors[1].value),
      colors[1].color,
      mapScaleValueType(colors[2].type),
      toNumber(colors[2].value),
      colors[2].color,
      ranges
    );
  }
}

/**
 * Apply dataBar rule (proportional bars).
 */
function applyDataBarRule(sheet: Worksheet, rule: DataBarRule): void {
  const ctx = getConditionalFormatContext(rule.range);
  if (!ctx) return;

  const { CF, ranges } = ctx;

  // Default min/max to auto-detect from data (matches Excel's default behavior)
  const min = rule.min ?? { type: 'min' as const };
  const max = rule.max ?? { type: 'max' as const };

  // Helper to convert value to number
  const toNumber = (value: number | string | undefined): number => {
    if (value === undefined) return 0;
    return typeof value === 'string' ? parseFloat(value) || 0 : value;
  };

  const dataBarRule = new CF.DataBarRule(
    mapScaleValueType(min.type),
    toNumber(min.value),
    mapScaleValueType(max.type),
    toNumber(max.value),
    rule.color,
    ranges
  );

  // showBarOnly(true) hides the cell value and shows only the bar
  // showValue in our schema means "show the cell value" so we invert it
  if (rule.showValue === false) {
    dataBarRule.showBarOnly(true);
  }

  sheet.conditionalFormats.addRule(dataBarRule);
}

/**
 * Apply conditional formatting rules from SheetSpec to a worksheet.
 * Rules are applied in array order (first rule = highest priority).
 */
export function applyConditionalFormats(
  sheet: Worksheet,
  rules: ConditionalFormatRule[]
): void {
  const GC = getSpreadJS();
  if (!GC || !rules || rules.length === 0) return;

  for (const rule of rules) {
    try {
      switch (rule.type) {
        case 'cellIs':
        case 'cellIsBetween':
          applyCellValueRule(sheet, rule);
          break;
        case 'expression':
          applyExpressionRule(sheet, rule);
          break;
        case 'containsText':
          applyContainsTextRule(sheet, rule);
          break;
        case 'colorScale':
          applyColorScaleRule(sheet, rule);
          break;
        case 'dataBar':
          applyDataBarRule(sheet, rule);
          break;
        default:
          logWarn(
            `Unknown conditional format rule type: ${(rule as any).type}`
          );
      }
    } catch (error) {
      logWarn(
        `Failed to apply conditional format rule: ${JSON.stringify(rule)}`
      );
    }
  }
}
